import React, { useCallback, useEffect, useMemo, useState } from "react";
import MappingTopBox from "./CreatorTools/MappingTool/components/MappingTopBox";
import LineItemOrderTable from "./CreatorTools/MappingTool/components/LineItemOrderTable";
import CatOrderTable, { CatGroup } from "./CreatorTools/MappingTool/components/CatOrderTable";
import CatOrderToolbar from "./CreatorTools/MappingTool/components/CatOrderToolbar";
import InsertLineItemModal from "./CreatorTools/MappingTool/components/InsertLineItemModal";
import CopyOrderFromButton from "./CreatorTools/MappingTool/components/CopyOrderFromButton";
import MatchCriteriaSelector from "./CreatorTools/MappingTool/components/MatchCriteriaSelector";
import { collection, CollectionReference, getDocs } from "firebase/firestore";
import { useFirestore, useFirestoreCollectionData } from "reactfire";
import { IDatabaseLineItem } from "./CreatorTools/LineItemDBCreationToolPage";
import { useLineItemOrderDefinitions } from "../hooks/useLineItemOrderDefinitions";
import { EstimateMatchCriteria, LineItemOrderObject } from "./types";
import { EstimateType, getRequiredScopesForEstimateType } from "./CreatorTools/MappingTool/utils/EstimateType";
import { IXactimateToolRowData } from "../shared/common/Azure";
import SharedUtils from "../shared/common/SharedUtils";
import { v4 as uuidv4 } from "uuid";

/**
 * Page used to look at, update, and create line item order definitions for different combinations of Customer + Estimate Type +
 * Carrier + TPA. The UI/UX should be the same when the user is created a new line item order definition as when the user is
 * updating an existing one.
 *
 * When the user selects a combination of values for the matching criteria, we see if there's already an existing line item order
 * definition that matches that criteria. If there is, then we load in the line item order objects for that definition. If not,
 * then we load in an empty page and we create a new order definition with the currently select matching criteria when the user
 * inserts a new order object.
 *
 * All state variables used by all components called by LineItemOrderPage are maintained in useLineItemOrderDefinitions. All read/write
 * interactions related to order definitions are done through useLineItemOrderDefinitions.
 */
const LineItemOrderPage: React.FC = () => {
	const {
		lineItemOrderDefinitions,
		isLoading: isLineItemOrderDefinitionsLoading,
		isUpdating: isLineItemOrderDefinitionsUpdating,
		saveLineItemOrderObjects,
		clearLineItemOrderObjects,
	} = useLineItemOrderDefinitions();

	const [matchCriteria, setMatchCriteria] = useState<EstimateMatchCriteria>({
		estimateType: EstimateType.MITIGATION,
	});
	const [showInsertModal, setShowInsertModal] = useState(false);
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
	const [catOrderMode, setCatOrderMode] = useState(false);
	const [catGroups, setCatGroups] = useState<CatGroup[]>([]);
	const [isLoadingCatMappings, setIsLoadingCatMappings] = useState(false);

	useEffect(() => {
		setCatOrderMode(false);
	}, [matchCriteria]);

	// Get all line items
	const firestore = useFirestore();
	const firestoreLineItemsCollectionRef = collection(firestore, "tools") as CollectionReference<IDatabaseLineItem>;
	const lineItems = useFirestoreCollectionData<IDatabaseLineItem>(firestoreLineItemsCollectionRef, { idField: "id" });

	// Get Xactimate mappings for CAT display
	const xactimateMappingsCollectionRef = collection(firestore, SharedUtils.getDbPath("tools/Xactimate/mapping")) as CollectionReference<IXactimateToolRowData>;
	const xactimateMappings = useFirestoreCollectionData<IXactimateToolRowData>(xactimateMappingsCollectionRef, { idField: "id" });

	const catByLineItemId = useMemo(() => {
		const map = new Map<string, string>();
		(xactimateMappings.data ?? []).forEach((m) => {
			if (m.selectedLineItemId && m.cat) {
				map.set(m.selectedLineItemId, m.cat);
			}
		});
		return map;
	}, [xactimateMappings.data]);

	const selByLineItemId = useMemo(() => {
		const map = new Map<string, string>();
		(xactimateMappings.data ?? []).forEach((m) => {
			if (m.selectedLineItemId && m.variable) {
				map.set(m.selectedLineItemId, m.variable);
			}
		});
		return map;
	}, [xactimateMappings.data]);

	const selectedLineItemOrderDefinition = useMemo(() => {
		if (isLineItemOrderDefinitionsLoading) {
			return;
		}

		return lineItemOrderDefinitions.find(
			(lineItemOrderDefinitions) =>
				lineItemOrderDefinitions.matchCriteria.organizationId === matchCriteria.organizationId &&
				lineItemOrderDefinitions.matchCriteria.estimateType === matchCriteria.estimateType &&
				lineItemOrderDefinitions.matchCriteria.carrierId === matchCriteria.carrierId &&
				lineItemOrderDefinitions.matchCriteria.tpaId === matchCriteria.tpaId,
		);
	}, [lineItemOrderDefinitions, isLineItemOrderDefinitionsLoading, matchCriteria]);

	const lineItemOrderObjects = useMemo(
		() => [...(selectedLineItemOrderDefinition?.lineItemOrderObjects || [])].sort((a, b) => a.index - b.index),
		[selectedLineItemOrderDefinition],
	);

	const updateLineItemOrderObjects = useCallback(
		async (updatedObjects: LineItemOrderObject[]) => {
			if (isLineItemOrderDefinitionsUpdating) {
				return;
			}
			await saveLineItemOrderObjects(selectedLineItemOrderDefinition?.uniqueId, matchCriteria, updatedObjects);
		},
		[selectedLineItemOrderDefinition, matchCriteria, saveLineItemOrderObjects, isLineItemOrderDefinitionsUpdating],
	);

	const handleOrderByCat = useCallback(async () => {
		setIsLoadingCatMappings(true);
		try {
			const collectionPath = SharedUtils.getDbPath("tools/Xactimate/mapping");
			const snapshot = await getDocs(collection(firestore, collectionPath) as CollectionReference<IXactimateToolRowData>);
			const mappings = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));

			// Build a map from lineItemId → cat
			const catByLineItemId = new Map<string, string>();
			mappings.forEach((m) => {
				if (m.selectedLineItemId && m.cat) {
					catByLineItemId.set(m.selectedLineItemId, m.cat);
				}
			});

			// When the order is empty, seed from all scope-matching line items
			let orderObjects = lineItemOrderObjects;
			if (orderObjects.length === 0) {
				const relevantScopes = getRequiredScopesForEstimateType(matchCriteria.estimateType).map((s) => s as string);
				orderObjects = (lineItems.data ?? [])
					.filter((item) => {
						if (item.deleted) return false;
						if (!item.scopes || item.scopes.length === 0) return true;
						return item.scopes.some((s) => relevantScopes.includes(s));
					})
					.map((item, index) => ({ uniqueId: uuidv4(), lineItemId: item.id, index }));
			}

			// Group by cat
			const groupMap = new Map<string, LineItemOrderObject[]>();
			orderObjects.forEach((obj) => {
				const cat = catByLineItemId.get(obj.lineItemId) ?? "Uncategorized";
				if (!groupMap.has(cat)) groupMap.set(cat, []);
				groupMap.get(cat)!.push(obj);
			});

			// Sort groups by predefined CAT order, with unknown/Uncategorized last
			const CAT_ORDER = [
				"FEE", "CON", "LAB", "TMP", "EXC", "SCF", "RFG", "SFG", "SDG", "MAS",
				"MTL", "STU", "TMB", "XST", "FEN", "LND", "DMO", "CPS", "MPR", "ELE",
				"LIT", "PLM", "TBA", "APP", "HVC", "MSD", "INS", "INM", "FRM", "STL",
				"DRY", "ACT", "PLA", "PNL", "TIL", "STR", "DOR", "WDA", "WDP", "WDR",
				"WDT", "WDV", "WDW", "WDS", "WPR", "FPL", "CAB", "PNT", "FCC", "FCR",
				"FCS", "FCT", "FCV", "FCW", "MBL", "FNC", "FNH", "ORI", "CAP", "CLM",
				"CGN", "CEL", "CHF", "CUP", "CWH", "CLN",
			];
			const sortedGroups: CatGroup[] = Array.from(groupMap.entries())
				.sort(([a], [b]) => {
					const aIdx = CAT_ORDER.indexOf(a);
					const bIdx = CAT_ORDER.indexOf(b);
					if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
					if (aIdx === -1) return 1;
					if (bIdx === -1) return -1;
					return aIdx - bIdx;
				})
				.map(([cat, items]) => ({ cat, items }));

			setCatGroups(sortedGroups);
			setCatOrderMode(true);
		} finally {
			setIsLoadingCatMappings(false);
		}
	}, [firestore, lineItemOrderObjects, matchCriteria, lineItems.data]);

	return (
		<div className="min-h-screen bg-gray-50">
			<MappingTopBox />
			<div className="mx-8 mt-4">
				<div className="mb-4">
					<h1 className="text-3xl font-bold text-gray-800 mb-2">Line Item Order</h1>
					<p className="text-gray-600">Configure the order of line items by dragging & dropping and inserting new line items.</p>
					<p className="text-gray-600">This ordering only affects the order of line items exported to Xactimate.</p>
				</div>
				<div className="flex items-end gap-4 mb-4">
					<MatchCriteriaSelector matchCriteria={matchCriteria} setMatchCriteria={setMatchCriteria} includeTestOrgs />
					<div className="ml-auto flex items-center gap-2">
						<CopyOrderFromButton
							lineItemOrderObjects={lineItemOrderObjects}
							currentMatchCriteria={matchCriteria}
							lineItemOrderDefinitions={lineItemOrderDefinitions}
							onCopy={(sourceObjects) => updateLineItemOrderObjects(sourceObjects)}
						/>
						<button
							onClick={() => setShowInsertModal(true)}
							className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
							title="Insert Line Item"
						>
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
							</svg>
							Insert Line Item
						</button>
					</div>
				</div>
				<CatOrderToolbar
				catOrderMode={catOrderMode}
				isLoadingCatMappings={isLoadingCatMappings}
				isLineItemOrderDefinitionsLoading={isLineItemOrderDefinitionsLoading}
				isLineItemOrderDefinitionsUpdating={isLineItemOrderDefinitionsUpdating}
				lineItemOrderObjects={lineItemOrderObjects}
				hasExistingDefinition={!!selectedLineItemOrderDefinition?.uniqueId}
				onOrderByCat={handleOrderByCat}
				onDeleteClick={() => setShowDeleteConfirm(true)}
			/>
				<div className="space-y-8">

					{isLineItemOrderDefinitionsLoading ? (
						<div className="text-gray-600">Loading line items...</div>
					) : catOrderMode ? (
						<CatOrderTable
							catGroups={catGroups}
							lineItems={lineItems.data || []}
							isUpdating={isLineItemOrderDefinitionsUpdating}
							onApply={async (reorderedItems) => {
								await updateLineItemOrderObjects(reorderedItems);
								setCatOrderMode(false);
							}}
							onCancel={() => setCatOrderMode(false)}
						/>
					) : (
						<LineItemOrderTable
							matchCriteria={matchCriteria}
							lineItemOrderObjects={lineItemOrderObjects}
							updateLineItemOrderObjects={updateLineItemOrderObjects}
							lineItems={lineItems.data || []}
							catByLineItemId={catByLineItemId}
							selByLineItemId={selByLineItemId}
						/>
					)}
				</div>
			</div>

			<InsertLineItemModal
				isOpen={showInsertModal}
				onClose={() => setShowInsertModal(false)}
				estimateType={matchCriteria.estimateType}
				lineItems={lineItems.data || []}
				lineItemOrders={lineItemOrderObjects}
				maxIndex={lineItemOrderObjects.length}
				onInsert={(newItem) => {
					const updatedObjects = [
						...lineItemOrderObjects.map((item) => (item.index >= newItem.index ? { ...item, index: item.index + 1 } : item)),
						newItem,
					];
					updateLineItemOrderObjects(updatedObjects);
				}}
			/>


			{showDeleteConfirm && selectedLineItemOrderDefinition?.uniqueId && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
					<div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
						<h3 className="text-lg font-semibold text-gray-900 mb-2">Delete all line items?</h3>
						<p className="text-gray-600 mb-6">
							This will remove all {lineItemOrderObjects.length} line items from this order. This cannot be undone.
						</p>
						<div className="flex justify-end gap-3">
							<button
								onClick={() => setShowDeleteConfirm(false)}
								className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={async () => {
									await clearLineItemOrderObjects(selectedLineItemOrderDefinition.uniqueId);
									setShowDeleteConfirm(false);
								}}
								className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
							>
								Delete all
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default LineItemOrderPage;
